import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Recording, RecordingCreateRequest, RecordingParams, RecordingUpdateRequest } from '@models/index';
import { TopicOrder, UpdateTopicOrderRequest } from '@models/marketing/topics/topics.model';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class RecordingsService {
  private readonly url = `${environment.bffUrl}/recordings`;

  constructor(private http: HttpClient) {}

  getAll(params?: RecordingParams): Observable<Recording[]> {
    return this.http.get<Recording[]>(this.url, { params });
  }

  getOne(id: string): Observable<Recording> {
    return this.http.get<Recording>(`${this.url}/${id}`);
  }

  add(recording: RecordingCreateRequest): Observable<Recording> {
    return this.http.post<Recording>(this.url, recording);
  }

  update(id: string, recording: RecordingUpdateRequest): Observable<Recording> {
    return this.http.put<Recording>(`${this.url}/${id}`, recording);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/${id}`);
  }

  activate(id: string): Observable<Recording> {
    return this.http.post<Recording>(`${this.url}/${id}/activate`, undefined);
  }

  deactivate(id: string): Observable<Recording> {
    return this.http.post<Recording>(`${this.url}/${id}/deactivate`, undefined);
  }

  addFieldExpert(recordingId: string, fieldExpertId: string): Observable<Recording> {
    return this.http.post<Recording>(`${this.url}/${recordingId}/field-experts`, { fieldExpertId });
  }

  deleteFieldExpert(recordingId: string, fieldExpertId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/${recordingId}/field-experts/${fieldExpertId}`);
  }

  setWorkloads(id: string, req: string[]): Observable<Recording> {
    return this.http.post<Recording>(`${this.url}/${id}/set-workloads`, req);
  }

  setMembershipLevels(id: string, req: string[]): Observable<Recording> {
    return this.http.post<Recording>(`${this.url}/${id}/set-membership-levels`, req);
  }

  addCommunities(recordingId: string, req: string[]): Observable<Recording> {
    return this.http.post<Recording>(`${this.url}/${recordingId}/set-communities`, req);
  }

  updateCommunity(recordingId: string, communityId: string, req: { membershipLevels: string[] }): Observable<Recording> {
    return this.http.put<Recording>(`${this.url}/${recordingId}/communities/${communityId}`, req);
  }

  deleteCommunity(recordingId: string, communityId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/${recordingId}/communities/${communityId}`);
  }

  approveCommunity(recordingId: string, communityId: string): Observable<Recording> {
    return this.http.post<Recording>(`${this.url}/${recordingId}/communities/${communityId}/approve`, undefined);
  }

  getTopicsOrder(): Observable<TopicOrder[]> {
    return this.http.get<TopicOrder[]>(`${this.url}/topics-order`);
  }

  setTopicsOrder(topicsOrder: UpdateTopicOrderRequest): Observable<TopicOrder[]> {
    return this.http.post<TopicOrder[]>(`${this.url}/topics-order`, topicsOrder);
  }
}
